import { useCallback, useEffect, useRef, useState } from "react";
import { FieldScoreApiError, scoreField, type ScoreDraftIdentity, type ScorableField } from "./qualityApi";

const AUTO_EVALUATE_DELAY_MS = 4_000;

export type FieldQuality =
    | { status: "idle" }
    | { status: "scoring" }
    | { status: "scored"; score: number; feedback: string }
    | { status: "error"; message: string };

const IDLE: FieldQuality = { status: "idle" };

interface DesiredEvaluation {
    value: string;
    generation: number;
    dueAt: number;
}

/** Scores Discover fields on blur/inference or after four seconds of idle input.
 *
 * Each field has one convergent worker. A value changed during an in-flight
 * evaluation supersedes (but does not race) the old value; the old response is
 * ignored and the worker evaluates the newest value. Draft-version conflicts
 * are retried because they are ordinary concurrent-save races, while genuine
 * provider failures remain visible and require an explicit retry/edit. */
export function useFieldQuality(
    token: string | null | undefined,
    options: {
        enabled?: boolean;
        ensurePersisted?: (
            field: ScorableField,
            value: string,
            forceSave?: boolean,
            staleDraftVersion?: number
        ) => Promise<ScoreDraftIdentity>;
    } = {}
) {
    const enabled = options.enabled ?? true;
    const [quality, setQuality] = useState<Record<ScorableField, FieldQuality>>({
        problem: IDLE,
        solution: IDLE,
        personas: IDLE
    });
    const requests = useRef<Partial<Record<ScorableField, AbortController>>>({});
    const workers = useRef<Partial<Record<ScorableField, Promise<boolean>>>>({});
    const timers = useRef<Partial<Record<ScorableField, ReturnType<typeof setTimeout>>>>({});
    const desired = useRef<Partial<Record<ScorableField, DesiredEvaluation>>>({});
    const generations = useRef<Record<ScorableField, number>>({ problem: 0, solution: 0, personas: 0 });
    const scoredText = useRef<Partial<Record<ScorableField, string>>>({});
    const mounted = useRef(true);

    const abort = useCallback((field: ScorableField) => {
        requests.current[field]?.abort();
        delete requests.current[field];
        const timer = timers.current[field];
        if (timer !== undefined) clearTimeout(timer);
        delete timers.current[field];
    }, []);

    useEffect(() => {
        const inFlight = requests.current;
        const scheduled = timers.current;
        mounted.current = true;
        return () => {
            mounted.current = false;
            Object.values(inFlight).forEach(controller => controller?.abort());
            Object.values(scheduled).forEach(timer => timer !== undefined && clearTimeout(timer));
        };
    }, []);

    const invalidate = useCallback(
        (field: ScorableField) => {
            abort(field);
            generations.current[field] += 1;
            delete desired.current[field];
            delete scoredText.current[field];
            setQuality(current => (current[field].status === "idle" ? current : { ...current, [field]: IDLE }));
        },
        [abort]
    );

    const runWorker = useCallback((field: ScorableField): Promise<boolean> => {
        const existing = workers.current[field];
        if (existing) return existing;

        const worker = (async (): Promise<boolean> => {
            let forcePersistence = false;
            let staleDraftVersion: number | undefined;
            while (mounted.current && enabled && token) {
                const target = desired.current[field];
                if (!target || target.value === "" || target.dueAt > Date.now()) return false;
                if (scoredText.current[field] === target.value) return true;

                const controller = new AbortController();
                let evaluatedDraft: ScoreDraftIdentity | undefined;
                requests.current[field] = controller;
                setQuality(current => ({ ...current, [field]: { status: "scoring" } }));
                try {
                    const draft = forcePersistence
                        ? await options.ensurePersisted?.(field, target.value, true, staleDraftVersion)
                        : await options.ensurePersisted?.(field, target.value, false);
                    evaluatedDraft = draft;
                    forcePersistence = false;
                    staleDraftVersion = undefined;
                    if (controller.signal.aborted) {
                        const latest = desired.current[field];
                        if (latest && latest.generation !== target.generation && latest.dueAt <= Date.now()) continue;
                        return false;
                    }
                    const result = await scoreField(field, target.value, token, { signal: controller.signal, draft });
                    if (controller.signal.aborted) {
                        const latest = desired.current[field];
                        if (latest && latest.generation !== target.generation && latest.dueAt <= Date.now()) continue;
                        return false;
                    }

                    const latest = desired.current[field];
                    if (!latest || latest.generation !== target.generation || latest.value !== target.value) {
                        // The value changed while the request was running. Only
                        // continue immediately when the newest value is due;
                        // otherwise its four-second idle timer owns the retry.
                        continue;
                    }
                    scoredText.current[field] = target.value;
                    setQuality(current => ({
                        ...current,
                        [field]: { status: "scored", score: result.score, feedback: result.feedback }
                    }));
                    return true;
                } catch (error) {
                    if (controller.signal.aborted) {
                        const latest = desired.current[field];
                        if (latest && latest.generation !== target.generation && latest.dueAt <= Date.now()) continue;
                        return false;
                    }
                    const latest = desired.current[field];
                    if (!latest || latest.generation !== target.generation || latest.value !== target.value) {
                        continue;
                    }
                    if (
                        error instanceof FieldScoreApiError &&
                        error.status === 409 &&
                        error.code === "DRAFT_VERSION_STALE"
                    ) {
                        // A concurrent autosave changed only the draft revision.
                        // Force one serialized save to obtain the authoritative
                        // latest revision, yield briefly, and retry this exact
                        // value without a fixed retry limit.
                        forcePersistence = true;
                        staleDraftVersion = evaluatedDraft?.draftVersion;
                        await new Promise(resolve => setTimeout(resolve, 100));
                        continue;
                    }
                    setQuality(current => ({
                        ...current,
                        [field]: {
                            status: "error",
                            message: error instanceof Error ? error.message : "Evaluation failed — try again."
                        }
                    }));
                    return false;
                } finally {
                    if (requests.current[field] === controller) delete requests.current[field];
                }
            }
            return false;
        })();
        workers.current[field] = worker;
        void worker.finally(() => {
            if (workers.current[field] === worker) delete workers.current[field];
        });
        return worker;
    }, [enabled, options.ensurePersisted, token]);

    const requestEvaluation = useCallback(
        (field: ScorableField, value: string, delayMs: number): Promise<boolean> => {
            const answer = value.trim();
            const existingTimer = timers.current[field];

            if (!enabled || !token || answer === "") {
                if (existingTimer !== undefined) clearTimeout(existingTimer);
                delete timers.current[field];
                generations.current[field] += 1;
                delete desired.current[field];
                delete scoredText.current[field];
                setQuality(current => (current[field].status === "idle" ? current : { ...current, [field]: IDLE }));
                return Promise.resolve(false);
            }
            if (scoredText.current[field] === answer) return Promise.resolve(true);

            const requestedDueAt = Date.now() + delayMs;
            const existingDesired = desired.current[field];
            if (
                existingDesired?.value === answer &&
                existingDesired.dueAt <= requestedDueAt
            ) {
                // An immediate blur/infer/delete evaluation may be followed by
                // the generic render-driven debounce for the same canonical
                // value. Never let that later schedule supersede or postpone
                // the request that is already due or in flight.
                return delayMs === 0
                    ? runWorker(field).then(() => scoredText.current[field] === answer)
                    : Promise.resolve(false);
            }

            if (delayMs === 0 && existingDesired?.value !== answer) {
                // Discrete actions such as removing multiple Personas should
                // not wait for an obsolete network evaluation to finish. The
                // worker observes the new generation and immediately moves to
                // the latest canonical list after the abort.
                requests.current[field]?.abort();
            }

            if (existingTimer !== undefined) clearTimeout(existingTimer);
            delete timers.current[field];

            const generation = generations.current[field] + 1;
            generations.current[field] = generation;
            desired.current[field] = { value: answer, generation, dueAt: requestedDueAt };
            delete scoredText.current[field];
            setQuality(current => (current[field].status === "idle" ? current : { ...current, [field]: IDLE }));

            if (delayMs === 0) return runWorker(field).then(() => scoredText.current[field] === answer);
            timers.current[field] = setTimeout(() => {
                delete timers.current[field];
                void runWorker(field);
            }, delayMs);
            return Promise.resolve(false);
        },
        [enabled, runWorker, token]
    );

    const evaluateAndWait = useCallback(
        (field: ScorableField, value: string): Promise<boolean> => requestEvaluation(field, value, 0),
        [requestEvaluation]
    );

    const evaluate = useCallback(
        (field: ScorableField, value: string) => {
            void evaluateAndWait(field, value);
        },
        [evaluateAndWait]
    );

    const schedule = useCallback(
        (field: ScorableField, value: string) => {
            void requestEvaluation(field, value, AUTO_EVALUATE_DELAY_MS);
        },
        [requestEvaluation]
    );

    return { quality, evaluate, evaluateAndWait, schedule, invalidate };
}
